import React, { useState } from "react";
import {
  CitySelect,
  CountrySelect,
  StateSelect,
} from "react-country-state-city";
import "react-country-state-city/dist/react-country-state-city.css";
import {
  Alert,
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  InputLabel,
  Link,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Link as RouterLink } from "react-router-dom";
import axios from "axios";

const interestAreas = [
  "Sales",
  "HR",
  "Engineering",
  "Creative",
  "Construction",
  "Interior Design",
  "Real Estate",
  "Marketing",
  "Operations",
];

const motivationOptions = [
  "Looking for a job right now",
  "Just networking",
  "Interested in company news",
];

const getPhoneError = (phone) => {
  if (!phone) return "";
  const digits = phone.match(/\d/g) || [];
  if (
    phone.length > 32 ||
    digits.length < 7 ||
    digits.length > 15 ||
    !/^\+?[0-9(). -]+$/.test(phone)
  ) {
    return "Enter a valid phone number using digits and common phone punctuation.";
  }
  return "";
};

const getLinkedInError = (value) => {
  if (!value) return "";
  if (value.length > 2048) return "LinkedIn URL must be 2048 characters or fewer.";
  try {
    const parsedUrl = new URL(value);
    return ["http:", "https:"].includes(parsedUrl.protocol) &&
      ["linkedin.com", "www.linkedin.com"].includes(parsedUrl.hostname)
      ? ""
      : "Enter a valid LinkedIn profile URL.";
  } catch {
    return "Enter a valid LinkedIn profile URL.";
  }
};

const sectionHeadingSx = {
  mt: 2,
  color: "#292720",
  fontFamily: 'Georgia, "Times New Roman", serif',
  fontSize: "1.4rem",
};

const locationSelectorSx = {
  "& .career-location-select": {
    border: "1px solid rgba(0, 0, 0, 0.23)",
    borderRadius: "4px",
    minHeight: 56,
  },
  "& .career-location-select:focus-within": {
    borderColor: "#292720",
    borderWidth: 2,
  },
  "& .career-location-input": {
    border: 0,
    background: "transparent",
    color: "#292720",
    font: "inherit",
    minHeight: 42,
    outline: 0,
    padding: "6px 14px",
    width: "100%",
    boxSizing: "border-box",
  },
  "& .career-location-input::placeholder": {
    color: "#716e65",
    opacity: 0.8,
  },
};

const CareersPage = ({ onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [formResetKey, setFormResetKey] = useState(0);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [privacyConsent, setPrivacyConsent] = useState(false);
  const [phoneContactConsent, setPhoneContactConsent] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneNumberError, setPhoneNumberError] = useState("");
  const [selectedInterestAreas, setSelectedInterestAreas] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [selectedCity, setSelectedCity] = useState("");
  const apiIp = process.env.REACT_APP_API_PUBLIC_AP;

  const handleSubmit = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);
    formData.delete("interestAreas");
    selectedInterestAreas.forEach((area) =>
      formData.append("interestAreas", area)
    );
    const location = [
      selectedCity.trim(),
      selectedRegion?.name,
      selectedCountry?.name,
    ]
      .filter(Boolean)
      .join(", ");
    formData.set("location", location);
    const cv = formData.get("cv");
    const name = String(formData.get("name") || "").trim();
    const linkedinUrl = String(formData.get("linkedinUrl") || "").trim();
    const skillsets = String(formData.get("skillsets") || "").trim();
    const currentJobTitle = String(formData.get("currentJobTitle") || "").trim();
    const namePattern = /^[\p{L}\p{M}][\p{L}\p{M}'’ .-]*$/u;
    const cvExtensions = [".pdf", ".doc", ".docx"];
    const cvExtension = cv instanceof File
      ? cv.name.slice(cv.name.lastIndexOf(".")).toLowerCase()
      : "";

    if (!namePattern.test(name) || name.length > 120) {
      setMessage({
        type: "error",
        text: "Enter a valid full name using letters and common name punctuation (up to 120 characters).",
      });
      return;
    }
    const phoneError = getPhoneError(phoneNumber.trim());
    if (phoneError) {
      setPhoneNumberError(phoneError);
      setMessage({ type: "error", text: phoneError });
      return;
    }
    const linkedinError = getLinkedInError(linkedinUrl);
    if (linkedinError) {
      setMessage({ type: "error", text: linkedinError });
      return;
    }
    if (
      formData.get("location").length > 120 ||
      currentJobTitle.length > 120 ||
      skillsets.length > 500
    ) {
      setMessage({
        type: "error",
        text: "Location, current job title, or skills exceed the allowed length.",
      });
      return;
    }
    if (cv instanceof File && cv.size > 5 * 1024 * 1024) {
      setMessage({
        type: "error",
        text: "Your CV must be 5 MB or smaller.",
      });
      return;
    }
    if (
      !(cv instanceof File) ||
      !cv.size ||
      !cvExtensions.includes(cvExtension)
    ) {
      setMessage({
        type: "error",
        text: "Attach a non-empty CV in PDF, DOC, or DOCX format.",
      });
      return;
    }
    setLoading(true);
    setMessage({ type: "", text: "" });

    try {
      await axios.post(`${apiIp}/api/email/career-application`, formData);
      setFormResetKey((key) => key + 1);
      setMessage({ type: "", text: "" });
      setPrivacyConsent(false);
      setPhoneContactConsent(false);
      setPhoneNumber("");
      setPhoneNumberError("");
      setSelectedInterestAreas([]);
      setSelectedCountry(null);
      setSelectedRegion(null);
      setSelectedCity("");
      onSuccess?.({
        title: "Application received",
        message:
          "Thank you for your interest in Homely Buildofiy. Our team has received your application and CV.",
      });
    } catch (error) {
      setMessage({
        type: "error",
        text:
          error.response?.data?.message ||
          "We couldn't submit your application. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: "70vh", backgroundColor: "#f6f4ef" }}>
      <Box
        sx={{
          px: { xs: 3, md: 8 },
          py: { xs: 8, md: 12 },
          color: "#f6f4ef",
          backgroundColor: "#292720",
        }}
      >
        <Typography
          sx={{
            mb: 2,
            color: "#d5bd91",
            fontSize: "0.68rem",
            fontWeight: 600,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
          }}
        >
          Build your future with us
        </Typography>
        <Typography
          component="h1"
          sx={{
            fontFamily: 'Georgia, "Times New Roman", serif',
            fontSize: { xs: "3rem", md: "4.5rem" },
            fontWeight: 400,
          }}
        >
          Careers at Homely
        </Typography>
        <Typography sx={{ mt: 2, maxWidth: 540, color: "rgba(246,244,239,0.76)", lineHeight: 1.8 }}>
          We are always glad to meet people who care about creating thoughtful
          spaces. Share your CV and, if you like, a little more about your
          experience and the opportunities you are interested in.
        </Typography>
      </Box>

      <Box
        key={formResetKey}
        component="form"
        onSubmit={handleSubmit}
        sx={{
          display: "grid",
          gap: 2.5,
          maxWidth: 680,
          mx: "auto",
          px: { xs: 3, md: 5 },
          py: { xs: 6, md: 8 },
        }}
      >
        <Typography
          component="h2"
          sx={{
            mb: 1,
            color: "#292720",
            fontFamily: 'Georgia, "Times New Roman", serif',
            fontSize: "1.8rem",
          }}
        >
          Submit your CV
        </Typography>
        <Typography sx={{ color: "#716e65", lineHeight: 1.7 }}>
          Name, email, CV, and privacy consent are required. The additional
          profile and preference details are optional and help our HR team
          understand your application.
        </Typography>
        {message.text && <Alert severity={message.type}>{message.text}</Alert>}
        <TextField
          name="name"
          label="Full name"
          autoComplete="name"
          inputProps={{ maxLength: 120 }}
          helperText="Use your name as it appears on your CV."
          required
          fullWidth
        />
        <TextField
          name="email"
          label="Email"
          type="email"
          autoComplete="email"
          inputProps={{ maxLength: 254 }}
          required
          fullWidth
        />
        <Typography component="h3" sx={sectionHeadingSx}>
          Contact &amp; identity
        </Typography>
        <TextField
          name="phoneNo"
          label="Phone number (optional)"
          type="tel"
          autoComplete="tel"
          inputProps={{
            maxLength: 32,
            pattern: "\\+?[0-9(). -]*",
            title: "Use digits and common phone punctuation only.",
          }}
          value={phoneNumber}
          error={Boolean(phoneNumberError)}
          helperText={
            phoneNumberError ||
            "Optional. Use digits, an optional leading +, spaces, parentheses, dots, or hyphens."
          }
          onChange={(event) => {
            const value = event.target.value;
            setPhoneNumber(value);
            const error = getPhoneError(value.trim());
            setPhoneNumberError(error);
            if (error || !value.trim()) setPhoneContactConsent(false);
            if (message.text) setMessage({ type: "", text: "" });
          }}
          fullWidth
        />
        <FormControlLabel
          control={
            <Checkbox
              name="phoneContactConsent"
              value="true"
              checked={phoneContactConsent}
              disabled={!phoneNumber.trim() || Boolean(phoneNumberError)}
              onChange={(event) =>
                setPhoneContactConsent(event.target.checked)
              }
              sx={{
                color: "#9a794b",
                "&.Mui-checked": { color: "#9a794b" },
              }}
            />
          }
          label="You may contact me by phone or text about urgent career opportunities. Enter a phone number above to opt in."
          sx={{
            alignItems: "flex-start",
            "& .MuiFormControlLabel-label": {
              pt: 0.8,
              color: "#716e65",
              fontSize: "0.82rem",
              lineHeight: 1.6,
            },
          }}
        />
        <Box sx={locationSelectorSx}>
          <Typography
            component="label"
            sx={{ display: "block", mb: 0.75, color: "#716e65", fontSize: "0.85rem" }}
          >
            Country (optional)
          </Typography>
          <CountrySelect
            containerClassName="career-location-select"
            inputClassName="career-location-input"
            name="country"
            aria-label="Country"
            placeHolder="Search and select a country"
            showFlag={false}
            defaultValue={selectedCountry || undefined}
            onChange={(country) => {
              setSelectedCountry(country);
              setSelectedRegion(null);
              setSelectedCity("");
            }}
            onTextChange={() => {
              setSelectedCountry(null);
              setSelectedRegion(null);
              setSelectedCity("");
            }}
          />
        </Box>
        <Box sx={locationSelectorSx}>
          <Typography
            component="label"
            sx={{ display: "block", mb: 0.75, color: "#716e65", fontSize: "0.85rem" }}
          >
            State / region (optional)
          </Typography>
          {selectedCountry ? (
            <StateSelect
              key={selectedCountry.id}
              countryid={selectedCountry.id}
              containerClassName="career-location-select"
              inputClassName="career-location-input"
              name="region"
              aria-label="State or region"
              placeHolder="Search and select a state or region"
              defaultValue={selectedRegion || undefined}
              onChange={(region) => {
                setSelectedRegion(region);
                setSelectedCity("");
              }}
              onTextChange={() => {
                setSelectedRegion(null);
                setSelectedCity("");
              }}
            />
          ) : (
            <TextField
              fullWidth
              disabled
              placeholder="Choose a country first"
              inputProps={{ "aria-label": "State or region" }}
            />
          )}
        </Box>
        <Box sx={locationSelectorSx}>
          <Typography
            component="label"
            sx={{ display: "block", mb: 0.75, color: "#716e65", fontSize: "0.85rem" }}
          >
            City (optional)
          </Typography>
          {selectedCountry && selectedRegion ? (
            <CitySelect
              key={`${selectedCountry.id}-${selectedRegion.id}`}
              countryid={selectedCountry.id}
              stateid={selectedRegion.id}
              containerClassName="career-location-select"
              inputClassName="career-location-input"
              name="city"
              aria-label="City"
              placeHolder="Search and select a city"
              defaultValue={selectedCity || undefined}
              onChange={(city) => setSelectedCity(city.name)}
              onTextChange={(event) => setSelectedCity(event.target.value)}
            />
          ) : (
            <TextField
              fullWidth
              disabled={!selectedCountry}
              placeholder={
                selectedCountry
                  ? "Choose a state or region to search cities"
                  : "Choose a country first"
              }
              value={selectedCity}
              onChange={(event) => setSelectedCity(event.target.value)}
              inputProps={{ "aria-label": "City" }}
            />
          )}
        </Box>
        <Typography component="h3" sx={sectionHeadingSx}>
          Professional profile
        </Typography>
        <TextField
          name="linkedinUrl"
          label="LinkedIn profile URL (optional)"
          type="url"
          placeholder="https://www.linkedin.com/in/your-profile"
          inputProps={{ maxLength: 2048 }}
          fullWidth
        />
        <TextField
          name="currentJobTitle"
          label="Current job title (optional)"
          inputProps={{ maxLength: 120 }}
          fullWidth
        />
        <TextField
          name="skillsets"
          label="Primary skillsets / expertise (optional)"
          placeholder="For example: Software Engineering, Product Marketing, Data Analytics"
          helperText="Separate skills with commas."
          inputProps={{ maxLength: 500 }}
          fullWidth
        />
        <Typography component="h3" sx={sectionHeadingSx}>
          Career preferences
        </Typography>
        <FormControl fullWidth>
          <InputLabel id="interest-areas-label">
            Areas of interest (optional)
          </InputLabel>
          <Select
            labelId="interest-areas-label"
            name="interestAreas"
            multiple
            value={selectedInterestAreas}
            onChange={(event) => {
              const value = event.target.value;
              setSelectedInterestAreas(
                typeof value === "string" ? value.split(",") : value
              );
            }}
            label="Areas of interest (optional)"
            renderValue={(selected) => selected.join(", ")}
          >
            {interestAreas.map((area) => (
              <MenuItem
                key={area}
                value={area}
                selected={selectedInterestAreas.includes(area)}
              >
                <Checkbox checked={selectedInterestAreas.includes(area)} />
                {area}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>Select one or more teams, if applicable.</FormHelperText>
        </FormControl>
        <FormControl fullWidth>
          <InputLabel id="work-modality-label">
            Preferred work modality (optional)
          </InputLabel>
          <Select
            labelId="work-modality-label"
            name="workModality"
            defaultValue=""
            label="Preferred work modality (optional)"
          >
            <MenuItem value="Remote">Remote</MenuItem>
            <MenuItem value="Hybrid">Hybrid</MenuItem>
            <MenuItem value="On-site">On-site</MenuItem>
          </Select>
        </FormControl>
        <FormControl fullWidth>
          <InputLabel id="job-level-label">Desired job level (optional)</InputLabel>
          <Select
            labelId="job-level-label"
            name="jobLevel"
            defaultValue=""
            label="Desired job level (optional)"
          >
            <MenuItem value="Entry-level">Entry-level</MenuItem>
            <MenuItem value="Mid-senior">Mid-senior</MenuItem>
            <MenuItem value="Director">Director</MenuItem>
            <MenuItem value="Executive">Executive</MenuItem>
          </Select>
        </FormControl>
        <FormControl component="fieldset">
          <Typography sx={{ color: "#292720", fontSize: "0.95rem" }}>
            What brings you here? (optional)
          </Typography>
          <FormGroup>
            {motivationOptions.map((motivation) => (
              <FormControlLabel
                key={motivation}
                control={
                  <Checkbox
                    name="motivations"
                    value={motivation}
                    sx={{
                      color: "#9a794b",
                      "&.Mui-checked": { color: "#9a794b" },
                    }}
                  />
                }
                label={motivation}
                sx={{ color: "#716e65" }}
              />
            ))}
          </FormGroup>
        </FormControl>
        <Typography component="h3" sx={sectionHeadingSx}>
          CV &amp; privacy
        </Typography>
        <TextField
          name="cv"
          label="CV / Resume (PDF, DOC or DOCX; up to 5 MB)"
          type="file"
          required
          fullWidth
          inputProps={{ accept: ".pdf,.doc,.docx" }}
          InputLabelProps={{ shrink: true }}
        />
        <FormControlLabel
          required
          control={
            <Checkbox
              name="privacyConsent"
              value="true"
              required
              checked={privacyConsent}
              onChange={(event) =>
                setPrivacyConsent(event.target.checked)
              }
              sx={{
                color: "#9a794b",
                "&.Mui-checked": { color: "#9a794b" },
              }}
            />
          }
          label={
            <span>
              I have read the{" "}
              <Link
                component={RouterLink}
                to="/careers/privacy"
                onClick={(event) => event.stopPropagation()}
                sx={{ color: "#75603b" }}
              >
                Career Applicant Privacy Notice
              </Link>{" "}
              and consent to Homely Buildofiy using my application details and
              CV as described there, including its retention terms.
            </span>
          }
          sx={{
            alignItems: "flex-start",
            "& .MuiFormControlLabel-label": {
              pt: 0.8,
              color: "#716e65",
              fontSize: "0.82rem",
              lineHeight: 1.6,
            },
          }}
        />
        <LoadingButton
          loading={loading}
          type="submit"
          variant="contained"
          sx={{
            justifySelf: "start",
            px: 3.5,
            py: 1.5,
            borderRadius: 0,
            backgroundColor: "#292720",
            fontSize: "0.7rem",
            fontWeight: 600,
            letterSpacing: "0.1em",
            "&:hover": { backgroundColor: "#49463d" },
          }}
        >
          Send your CV
        </LoadingButton>
      </Box>
    </Box>
  );
};

export default CareersPage;
